import React, { FC, useState, useEffect } from 'react'
import { IonContent, IonToast, IonSpinner } from '@ionic/react'
import { payMobx, bankCardMobx } from '@mobxStore'
import { payChannelMobx } from '@mobx/mall/payment'

interface IProps {
  payRechargeFlag?: boolean;
  onAuthorize?(card: TBankCardInfo): void;
  multiUse?: boolean;
}

const HeartlandTokenizer: FC<IProps> = ({ payRechargeFlag, onAuthorize, multiUse }) => {
  const [form, setForm] = useState<any>()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [authorizing, setAuthorizing] = useState<boolean>(false)

  const auth = async (token: string, details: Record<string, string>) => {
    if (!details.cardSecurityCode) {
      setErrorMessage('CVV is required.')
      return
    }

    form && form.dispose()
    if (multiUse) {
      setAuthorizing(true)
      try {
        let newCard: IMutBankCardInfo
        if (payRechargeFlag) {
          const { id, number } = await payMobx.authBankCard('HeartLandBankCard', token)
          newCard = {
            tokenType: 'multi',
            creditCardNo: id,
            creditCardNumber: number,
          }
        } else {
          newCard = await bankCardMobx.authorize('HeartLandBankCard', token, details)
        }
        onAuthorize && onAuthorize(newCard)
      } catch (e) {
        console.error('error auth card', e)
        setErrorMessage(e.message)
      } finally {
        setAuthorizing(false)
      }
    } else {
      onAuthorize({
        tokenType: 'single',
        token,
      })
    }
  }

  const loadForm = () => {
    const GlobalPayments = (window as any).GlobalPayments
    GlobalPayments.configure({
      publicApiKey: payChannelMobx.channelAccount.HeartLandBankCard.publicApiKey,
      enableAutocomplete: true,
    })

    let loaded = false

    const cardForm = GlobalPayments.ui.form({
      fields: {
        'card-number': {
          placeholder: 'Card number',
          target: '#credit-card-card-number'
        },
        'card-expiration': {
          placeholder: 'MM / YYYY',
          target: '#credit-card-card-expiration'
        },
        'card-cvv': {
          placeholder: 'CVV',
          target: '#credit-card-card-cvv'
        },
        'submit': {
          target: '#credit-card-submit',
          text: i18n.t('pay.confirmAddBankCardNoChinese'),
        }
      },
      styles: {
        'input': {
          'appearance': 'none',
          '-webkit-appearance': 'none',
          'display': 'inline-block',
          'outline': '0',
          'box-sizing': 'border-box',
          'border': '1px solid #F4F4F4',
          'border-radius': '8px',
          'height': '56px',
          'padding': '0 15px',
          'font-size': '14px',
          'color': '#161616',
          'background-color': '#F4F4F4',
        },
        '#secure-payment-field.submit': {
          'appearance': 'none',
          '-webkit-appearance': 'none',
          'margin': '10px 16px',
          'outline': 'none',
          'border': 'none',
          'border-radius': '999px',
          'height': '48px',
          'line-height': '48px',
          'font-size': '14px',
          'font-weight': '600',
          'background': 'linear-gradient(135deg,rgba(255,81,81,1) 0%,rgba(224,3,36,1) 100%)',
          'color': '#fff'
        }
      }
    })
    cardForm.on('token-success', (resp) => {
      console.debug('token-success', resp)
      if (resp.paymentReference) {
        auth(resp.paymentReference, resp.details)
      }
    })
    cardForm.on('token-error', (resp) => {
      console.debug('token-error', resp)
      if (resp.error && resp.reasons && resp.reasons.length > 0) {
        setErrorMessage(resp.reasons[0].message)
      }
    })
    cardForm.on('load', () => {
      loaded = true
      setTimeout(() => {
        setForm(cardForm)
      }, 0)
    })
    setTimeout(() => {
      if (!loaded) {
        console.warn('load form timeout, reloading...')
        cardForm && cardForm.dispose()
        loadForm()
      }
    }, 10000)
  }

  useEffect(() => {
    const script = document.createElement('script')
    script.src = '/js/lib/globalpayments/globalpayments.js'
    script.onload = loadForm
    document.body.appendChild(script)
  }, [])

  return (
    <>
      <IonToast
        position='top'
        color='danger'
        isOpen={!!errorMessage}
        onDidDismiss={() => setErrorMessage('')}
        message={errorMessage}
        duration={2000}
      />
      <IonContent className='mall-heartland-tokenizer'>
        {!form && (
          <div className='loading-wrapper'>
            <IonSpinner />
            <div>{i18n.t('pay.jumpToProcessorPage')}</div>
          </div>
        )}
        {authorizing && (
          <div className='loading-wrapper'>
            <IonSpinner />
            <div>{i18n.t('pay.authorizingCard')}</div>
          </div>
        )}
        <div id='mall-heartland-tokenizer-form'>
          <div id='credit-card-card-number' />
          <div id='credit-card-card-expiration' />
          <div id='credit-card-card-cvv' />
        </div>
        <div className='processor-power-by'>{i18n.t('pay.powerByProcessor')}</div>
        <div id='credit-card-submit' />
      </IonContent>
    </>
  )
}

export default HeartlandTokenizer
